//product 
import * as THREE from 'three';
import Tween from 'root/animator/tween/tween';
import { layers } from '../constants';
import { dispatchSetChanging } from '../utils';
import textImage from '../assets/text.png' ;
import Ease from 'root/animator/tween/easing';

class Object5 {
  instance;
  geometry;
  material;
  shader;
  textTexture;
  textGeometry;
  textShader;
  textMaterial;
  textMesh;

  state = {
    changing: false,
    visible: false,
    move: 0,
  };

  constructor(scene) {
    this.geometry = new THREE.PlaneBufferGeometry(4, 4);

    this.shader = {
      uniforms: {
        iResolution: {
          value: {
            x: window.innerWidth * window.devicePixelRatio,
            y: window.innerHeight * window.devicePixelRatio,
          },
        },
        iTime: { value: 0 },
        iAlpha: { value: 0 },
      },
      vertexShader: `
            void main()	{
                gl_Position = vec4( position, 1.0 );
            }`,
      fragmentShader: `
            uniform float iTime;
            uniform vec2 iResolution;
            uniform float iAlpha;
        
            vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
            vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
        
            float snoise(vec3 v){ 
                const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
                const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
                vec3 i  = floor(v + dot(v, C.yyy) );
                vec3 x0 =   v - i + dot(i, C.xxx) ;
                vec3 g = step(x0.yzx, x0.xyz);
                vec3 l = 1.0 - g;
                vec3 i1 = min( g.xyz, l.zxy );
                vec3 i2 = max( g.xyz, l.zxy );
                vec3 x1 = x0 - i1 + 1.0 * C.xxx;
                vec3 x2 = x0 - i2 + 2.0 * C.xxx;
                vec3 x3 = x0 - 1. + 3.0 * C.xxx;
                i = mod(i, 289.0 ); 
                vec4 p = permute( permute( permute( 
                        i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                        + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                        + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
                float n_ = 1.0/7.0; // N=7
                vec3  ns = n_ * D.wyz - D.xzx;
        
                vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)
        
                vec4 x_ = floor(j * ns.z);
                vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)
        
                vec4 x = x_ *ns.x + ns.yyyy;
                vec4 y = y_ *ns.x + ns.yyyy;
                vec4 h = 1.0 - abs(x) - abs(y);
        
                vec4 b0 = vec4( x.xy, y.xy );
                vec4 b1 = vec4( x.zw, y.zw );
        
                vec4 s0 = floor(b0)*2.0 + 1.0;
                vec4 s1 = floor(b1)*2.0 + 1.0;
                vec4 sh = -step(h, vec4(0.0));
        
                vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
                vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
        
                vec3 p0 = vec3(a0.xy,h.x);
                vec3 p1 = vec3(a0.zw,h.y);
                vec3 p2 = vec3(a1.xy,h.z);
                vec3 p3 = vec3(a1.zw,h.w);
                vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
                p0 *= norm.x;
                p1 *= norm.y;
                p2 *= norm.z;
                p3 *= norm.w;
                vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
                m = m * m;
                return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                            dot(p2,x2), dot(p3,x3) ) );
            }
        
            vec2 wnoise(vec2 pos, float t, float curv, float a, float b) {
                pos.x += curv * sin(t + a * pos.y) + t * b;
                pos.y += curv * cos(t + a * pos.x);
                pos.y += curv * sin(t + a * pos.x) + t * b;
                pos.x += curv * cos(t + a * pos.x);
                pos.x += curv * sin(t + a * pos.y) + t * b;
                pos.y += curv * cos(t + a * pos.x);
                pos.y += curv * sin(t + a * pos.x) + t * b;
                pos.x += curv * cos(t + a * pos.x);
                return 0.5 + 0.5 * cos(pos + vec2(1., 2.));
            }
            
            vec2 wnoise2(vec2 pos, float t, float curv) {
                pos.x += curv * sin(2.0*t+1.5 * pos.y)+t*0.5;
                pos.y += curv * cos(2.0*t+1.5 * pos.x);
                return 0.5 + 0.5*cos(iTime+pos.xy+vec2(0,2));
            }
        
            float noise( in vec2 p ){
                return 0.7*wnoise( p*2., 3.*iTime, .3, 1.8, .4 ).x + 0.3*snoise( vec3(p*1.2, 2.*iTime) );
            }
                
            float sdSegment( in vec2 p, in vec2 a, in vec2 b ) {
                vec2 pa = p-a, ba = b-a;
                float h = clamp( dot(pa,ba)/dot(ba,ba), 0.0, 1.0 );
                return length( pa - ba*h );
            }
        
            vec3 hsv2rgb(vec3 c)
            {
                vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
                vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
                return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
            }
            
            void main() {
                vec2 vUv = gl_FragCoord.xy/iResolution.xy;
                vec2 uv = (2. * gl_FragCoord.xy - iResolution.xy) / iResolution.y;
                
                uv *= 1.2;
                float w = pow(mix(.3, 1., noise(vec2(uv.x*2.5, uv.y*1.3) + .5 + .5*noise(uv))), 2.);
                
                vec2 v = 2. * vUv - 1.;
                v.y += .07 * sin(v.x*10.);
                float h = .9*smoothstep(.0, .8, sqrt(max(0., .7 - sdSegment(vec2(v.x * iResolution.x / iResolution.y, v.y), vec2(-.6, 0.), vec2(.6,0.)))));

                // flame frame
                // v = 2. * vUv - 1.;
                // v.x = .8*v.x - .42;
                // v.y += .03 * sin(v.x*10.+1.2) - .06*(2.*vUv.x-1.) + .00;
                // h = max(h, 1.2*max(-.2, .6 - sdSegment(vec2(v.x * iResolution.x / iResolution.y, v.y+1.1), vec2(-.6, 0.), vec2(.6,0.))));
                // v = 2. * vUv - 1.;
                // v.x = .8*v.x + .42;
                // v.y += .03 * sin(v.x*10.-1.2) - .06*(2.*vUv.x-1.) - .00;
                // h = max(h, 1.2*max(-.2, .6 - sdSegment(vec2(v.x * iResolution.x / iResolution.y, v.y-1.1), vec2(-.6, 0.), vec2(.6,0.))));
                
                vec2 q = wnoise2(uv*1.5, iTime*1.4, .4);
                float hue = (q.x+q.y)/2.;
                
                vec3 col = (.8*h + .3 * w-.2) * hsv2rgb(vec3(.45 + .13*hue, 0.47, 0.6));
                col = smoothstep(.0, .8, col)-.1;
                gl_FragColor = vec4(1.5*col, iAlpha * 0.5);
            }`,
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.shader.uniforms,
      vertexShader: this.shader.vertexShader,
      fragmentShader: this.shader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.instance = new THREE.Mesh(this.geometry, this.material);
    this.instance.layers.enable(layers.ENTIRE_SCENE);
    scene.add(this.instance);

    //

    this.textTexture = new THREE.TextureLoader().load( textImage );
    this.textTexture.wrapS = THREE.RepeatWrapping;
	this.textTexture.wrapT = THREE.RepeatWrapping;
    this.textShader = {
      uniforms: {
        textTexture: { value: this.textTexture },
        iResolution: {
            value: {
              x: window.innerWidth * window.devicePixelRatio,
              y: window.innerHeight * window.devicePixelRatio,
            },
          },
        iTime: { value: 0 },
        uAlpha: { value: 0 }
      },
      vertexShader: `
        void main()	{
            gl_Position = vec4( position, 1.0 );
        }`,
      fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;
        uniform float uAlpha;
        uniform sampler2D textTexture;
        void main() {
            vec2 vUv = (gl_FragCoord.xy - .5 * iResolution.xy) / iResolution.y;
            vUv.y += .07*sin(vUv.x*4.);
            vUv.y += .6;
            vUv.x += iTime*.1;
            vUv.x += iTime*.1;
            vec4 col = texture2D(textTexture, vUv);
            gl_FragColor = vec4(col.xyz, col.w * uAlpha);
        }`,
    };
    this.textGeometry = new THREE.PlaneBufferGeometry(4, 4);
    this.textMaterial = new THREE.ShaderMaterial({
      uniforms: this.textShader.uniforms,
      vertexShader: this.textShader.vertexShader,
      fragmentShader: this.textShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.textMesh = new THREE.Mesh(this.textGeometry, this.textMaterial);
    this.textMesh.layers.enable(layers.ENTIRE_SCENE);
    this.textMesh.name = 'PlaneText2';
    scene.add(this.textMesh);
    this.instance.visible = false
    this.textMesh.visible = false
  }

  toggle({ opacity = 1, duration = 1000, delay = 0 }) {
    const from = {
      opacity: null,
    };
    const to = {
      opacity: null,
    };

    if (opacity === 1) {
      from.opacity = 0;
      to.opacity = 1;
    } else {
      from.opacity = 1;
      to.opacity = 0;
    }

    Tween.to(from, to, {
      duration,
      renderDelay: 10,
      delay,
      onChange: function (context) {
        this.instance.material.uniforms.iAlpha.value = context.value.opacity;
      }.bind(this),
      onComplete: function () {
        this.state.changing = false;
        dispatchSetChanging(false, 0);
        this.state.visible = !!opacity;
      }.bind(this),
    });
  }

  render__moveIn() {
    this.render__move(
        {
            alpha: 0
        },
        {
            alpha: 1
        },
        () => {},
        Ease.Out,
    )
}
render__moveOut() {
    this.render__move(
        {
            alpha: 1
        },
        {
            alpha: 0
        },
        () => {},
        Ease.In,
        0,
        500
    )
}
  render__move(from: any, to: any,  onComplete = () => {}, ease = Ease.InCubic, delay = 400, duration = 500) {
    Tween.to(from, to,
        {
            renderDelay: 10,
            delay,
            duration,
            ease: ease,
            onChange: function(context) {
                this.textMaterial.uniforms.uAlpha.value = context.value.alpha
            }.bind(this),
            onComplete: function(context) { 
                this.textMaterial.uniforms.uAlpha.value = to.alpha
                onComplete()
            }.bind(this)
        }
    )
}

  resize() {
    this.material.uniforms[ 'iResolution' ].value.x = window.innerWidth * window.devicePixelRatio;
    this.material.uniforms[ 'iResolution' ].value.y = window.innerHeight * window.devicePixelRatio;
    this.textMaterial.uniforms[ 'iResolution' ].value.x = window.innerWidth * window.devicePixelRatio;
    this.textMaterial.uniforms[ 'iResolution' ].value.y = window.innerHeight * window.devicePixelRatio;
    }

  render({ visible = false, move = null }) {
    if (!this.state.changing) {
      if (visible && !this.state.visible) {
        this.state.changing = true;
        dispatchSetChanging(true, 0);
        this.toggle({ opacity: 1, delay: 1000 });
      } else if (!visible && this.state.visible) {
        this.state.changing = true;
        dispatchSetChanging(true, 0);
        this.toggle({ opacity: 0 });
        if (this.state.move === 2) {
          this.render__moveOut()
        }
        this.state.move = null
      }
    }

    if (visible && this.state.visible) {
      if (move !== null) {
        if (move === 1 && this.state.move !== 1) {
          this.state.changing = true;
          dispatchSetChanging(true, 0);
          this.state.move = move;
          this.render__moveOut()
          setTimeout(() => {
            dispatchSetChanging(false, 0);
            this.state.changing = false;
          }, 2000);
        } else if (move === 2 && this.state.move !== 2) {
          this.state.changing = true;
          dispatchSetChanging(true, 0);
          this.state.move = move;
          this.render__moveIn()
          setTimeout(() => {
            dispatchSetChanging(false, 0);
            this.state.changing = false;
          }, 2000);
        }
      }
    }

    if (visible || this.state.visible) {
      const t = performance.now() / 1000;
      this.material.uniforms.iTime.value = t;
      this.textMaterial.uniforms.iTime.value = t;
      this.instance.visible = true
      this.textMesh.visible = true
    } else {
        this.instance.visible = false
        this.textMesh.visible = false
    }
  }
}

export default Object5;